/** @format */

import React from 'react';

import {useTheme} from '@mui/material/styles';

import {shortNumber} from 'helpers/numberHelpers';
import getTokenWithSpace from 'pages/Home/DigitalCast/helpers/getTokenWithSpace';

import {type BuiltChartData} from '../../buildChartData';
import {OverviewChart} from '../../OverviewChart';
import {type KpiRowModel} from '../KpiTable';
import {getValueColor} from '../getValueColor';
import {MetricTextBlock} from '../MetricTextBlock';

import {ChartArea, ChartsRoot, KpiLabel, KpiRow, RowDivider, TextBlock} from './styles';

/**
 * Recharts-ready data for a single KPI's dynamic chart — exactly what buildChartData produces.
 * Aliased to the single source so a new BuiltChartData field can't silently drop here. A KPI uses
 * a free `string` series id (the parent decides the namespace).
 */
export type KpiChartData = BuiltChartData<string>;

export interface KpiChartRow extends KpiRowModel {
  // Supplied by MetricsBlock for dynamic mode; undefined → empty chart area (parent still assembling).
  chart?: KpiChartData;
}

interface Props {
  rows: KpiChartRow[];
  planPeriodLabel: string;
}

const KpiChartRender = ({chart, color}: {chart?: KpiChartData; color?: string}) => {
  const chartObj = chart?.data.charts[0];
  if (!chart || !chartObj) {
    return null;
  }

  return (
    <OverviewChart
      chart={chartObj}
      tooltipData={chart.data.tooltipDataList}
      height={chart.heightParams.boxHeight}
      axisStart={chart.axisStart}
      axisEnd={chart.axisEnd}
      todayRatio={chart.todayRatio}
      todayLabel={chart.todayLabel}
      valueLabels={chart.valueLabels}
      forecastValueLabels={chart.forecastValueLabels}
      color={color}
    />
  );
};

const KpiCharts = ({rows, planPeriodLabel}: Props) => {
  const theme = useTheme();

  if (rows.length === 0) {
    return null;
  }

  return (
    <ChartsRoot>
      {rows.map((row, index) => {
        const {label, kind, progress, lowerIsBetter, showForecast, unit, unitNeedsSpace, chart} =
          row;

        // Chart inherits the «Выполнение» color (fact vs plan). No plan → undefined → default color.
        const factColor = getValueColor(progress.factToDate, progress.planToDate, lowerIsBetter);
        const chartColor = progress.planForPeriod > 0 ? factColor(theme) : undefined;

        // Unit suffix: ' ₽' (money), '%' (ДРР/CTR/ROMI…), '' (counts).
        const suffix = getTokenWithSpace({token: unit, needSpaceBeforeToken: unitNeedsSpace});
        const formatValue = (value: number) => `${shortNumber(value)}${suffix}`;

        return (
          <React.Fragment key={label}>
            {index > 0 && <RowDivider />}

            <KpiRow>
              <TextBlock>
                <KpiLabel>{label}</KpiLabel>

                <MetricTextBlock
                  progress={progress}
                  lowerIsBetter={lowerIsBetter}
                  // "План на сегодня" only for additive KPIs — ratio plan is constant (redundant row).
                  showPlanToDate={kind === 'additive'}
                  showForecast={showForecast}
                  planPeriodLabel={planPeriodLabel}
                  formatValue={formatValue}
                />
              </TextBlock>

              <ChartArea sx={{minHeight: chart?.heightParams.overallHeight}}>
                <KpiChartRender chart={chart} color={chartColor} />
              </ChartArea>
            </KpiRow>
          </React.Fragment>
        );
      })}
    </ChartsRoot>
  );
};

export {KpiCharts};
